'use client';

import IconArrowRightBlue from '@/assets/icons/catalogs/icon-arrow-right-blue.svg';
import IconBts from '@/assets/icons/product/icon-bts.svg';
import IconCopy from '@/assets/icons/product/icon-copy.svg';
import IconExport from '@/assets/icons/product/icon-export.svg';
import IconGift from '@/assets/icons/product/icon-gift.svg';
import IconKuryer from '@/assets/icons/product/icon-kuryer.svg';
import IconShop from '@/assets/icons/product/icon-shop.svg';
import IconStar from '@/assets/icons/product/icon-star.svg';
import IconYandex from '@/assets/icons/product/icon-yandex.svg';
import IconInfo from '@/assets/icons/product/Info.svg';
import {
  Breadcrumbs,
  CategoryCard,
  CustomCitiesSelect,
  StickyHeader,
} from '@/components';

import { config } from '@/apollo/config';
import AvailabilityIndicator from '@/components/ui/availabilityIndicator';
import {
  Maybe,
  ProductDefaultTabEnum,
  ProductFilterInput,
  useAddViewedVariantMutation,
  useCategoryListQuery,
  useCityShippingMethodsListQuery,
  useDefaultChannelQuery,
  useDraftOrderCreateMutation,
  useDraftOrderLineCreateMutation,
  useDraftOrderQuery,
  useMeQuery,
  useProductQuery,
  useProductVariantQuery,
  useStoreBranchesQuery,
  useUserDraftOrdersQuery,
} from '@/graphql';
import { AttributeValue, ProductVariant } from '@/graphql/graphql';
import convertToReadable from '@/helpers/convertToReadable';
import { decodeBase64Id } from '@/helpers/decodeBase64Id';
import { ReviewsText } from '@/helpers/reviewsText';
import useHasMounted from '@/hooks/useHasMounted';
import { useLanguageCode } from '@/hooks/useLanguageCode';
import { Link, usePathname, useRouter } from '@/i18n/navigation';
import { useCityStore } from '@/zustand/cityStore';
import { useGuestStore } from '@/zustand/guestStore';
import useAuthModal from '@/zustand/useAuthModal';
import useMobileMenu from '@/zustand/useMobileMenu';
import useUserStore from '@/zustand/useUser';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useWindowSize } from 'react-use';
import { Button, Rate } from 'rsuite';
import ProductCarousel from './components/carousel';
import InStoreAvailability from './components/magazines';
import ReccomendSection from './components/recommend-section';
import SecondaryInfo from './components/secondary-info';
import Favorite from './favorite';
import styles from './product.module.scss';
import { toast } from 'react-toastify';
import DefaultProductPlaceholder from '@/assets/images/Yellow.png';
import { ProductImageArray } from '@/components/ui/card-second';

interface ProductDetailsProps {
  productId: string;
}

interface BreadcrumbItem {
  title: string | undefined | null;
  link: string;
}

interface Color {
  value: Maybe<string> | AttributeValue | undefined;
  name: string | null | undefined;
}

const placeholderImageForCarousel: ProductImageArray = [
  {
    __typename: 'ProductMedia',
    id: 'default-placeholder',
    alt: 'Default product image',
    url: DefaultProductPlaceholder.src,
  },
];

const ProductDetails: React.FC<ProductDetailsProps> = ({ productId }) => {
  const currentURL = typeof window !== 'undefined' ? window.location.href : '';
  // orderLinesCreate
  const [currentProductId, setCurrentProductId] = useState<string>(productId);
  const { setOpen: setMobileMenuOpen } = useMobileMenu();
  const { setPopoverOpen } = useAuthModal();
  const { user } = useUserStore();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null
  );
  const [city, setCity] = useState('');
  const [draftOrderLoading, setDraftOrderLoading] = useState(false);
  const [draftOrderLineLoading, setDraftOrderLineLoading] = useState(false);
  const [isInCart, setIsInCart] = useState(false);
  const pathname = usePathname();
  const inStoreAvailabilityRef = useRef<HTMLDivElement | null>(null);
  const similarProductsFilter: ProductFilterInput | undefined =
    selectedCategoryId ? { categories: [selectedCategoryId] } : undefined;
  const router = useRouter();
  const hasMounted = useHasMounted();
  const t = useTranslations('Components.product');
  const tBreadcrumb = useTranslations('Breadcrumbs');
  const locale = useLocale();
  const mobileRef = useRef<HTMLDivElement | null>(null);
  const desktopRef = useRef<HTMLDivElement | null>(null);
  const isLoggedIn = Boolean(user?.id);
  const orderId = useGuestStore(state => state.guestOrderId);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const { cityValue } = useCityStore();
  const languageCode = useLanguageCode();

  const {
    data: productVariantData,
    loading,
    refetch,
  } = useProductVariantQuery({
    variables: {
      id: productId,
      channel: 'default-channel',
      city: cityValue,
      languageCode: languageCode,
    },
    skip: !currentProductId,
  });
  const { data: draftOrdersData } = useUserDraftOrdersQuery({
    variables: {
      userId: user ? user?.id : '',
    },
    skip: !user,
  });

  const { data: guestDraftOrdersData, refetch: refetchGuestDraftOrdersData } =
    useDraftOrderQuery({
      variables: {
        id: orderId || '',
      },
      // skip: isLoggedIn || !orderId, // Skip if user is logged in or there's no orderId
    });

  const price =
    productVariantData?.productVariant?.pricing?.price?.net?.amount ?? 0;
  const priceUndiscounted =
    productVariantData?.productVariant?.pricing?.priceUndiscounted?.net
      ?.amount ?? 0;
  const discount =
    productVariantData?.productVariant?.pricing?.discount?.net?.amount ?? 0;

  const [createDraftOrderLine] = useDraftOrderLineCreateMutation();
  const [createDraftOrder] = useDraftOrderCreateMutation();

  const { data: productData, loading: productLoading } = useProductQuery({
    variables: {
      id: productVariantData?.productVariant?.product?.id,
      languageCode: languageCode,
    },
  });

  const { data: userData, loading: userLoading } = useMeQuery({
    skip: !user,
    context: {
      requiresAuth: true,
    },
  });

  const { data: channelData, loading: channelLoading } =
    useDefaultChannelQuery();

  const [addViewedVariant] = useAddViewedVariantMutation();

  const { data: similarProductData, loading: similarProductLoading } =
    useProductQuery({
      variables: {
        id: productVariantData?.productVariant?.product?.id,
        similarProductsFilter: similarProductsFilter,
        languageCode: languageCode,
      },
    });

  const categoriesFilter = productVariantData?.productVariant?.product?.brand
    ?.id
    ? { brand: productVariantData?.productVariant?.product?.brand?.id }
    : {};

  const { data: categoriesData, loading: categoriesLoading } =
    useCategoryListQuery({
      skip: !productVariantData?.productVariant?.product?.brand?.id,
      variables: {
        first: 6,
        filter: categoriesFilter,
        languageCode: languageCode,
      },
    });

  const { loading: isBranchLoading, data: branchData } = useStoreBranchesQuery({
    variables: {
      first: 10,
      after: null,
    },
  });

  const { data: cityShippingData, loading: cityShippingLoading } =
    useCityShippingMethodsListQuery({
      variables: {
        first: 1,
        filter: {
          city: cityValue,
        },
      },
    });
  const cityShippingMethod =
    cityShippingData?.cityShippingMethods?.edges[0]?.node;

  const [breadData, setBreadData] = useState<BreadcrumbItem[]>([
    {
      title: tBreadcrumb('main'),
      link: '/',
    },
  ]);
  const { width } = useWindowSize();
  const [codeText, setCodetext] = useState<string>('0123ABC90');
  const bonus = productVariantData?.productVariant?.pricing?.bonusesAmount;

  const existingDraftOrder = draftOrdersData?.userDraftOrders?.find(
    edge => edge?.isFastBuy === false
  );
  const existingGuestDraftOrder =
    guestDraftOrdersData?.draftOrder?.isFastBuy === false;

  const handleCategorySelected = (categoryId: string | undefined) => {
    setSelectedCategoryId(categoryId || null);
  };

  const handleClick = async (id?: string) => {
    const collectionPointId = id ?? null;
    if (productVariantData?.productVariant?.availability === 'OUT_OF_STOCK') {
      toast.error(`Товара нет в наличии.`);
      return;
    }
    if (productVariantData?.productVariant?.availability === 'COMING_SOON') {
      toast.info('Товара скоро появится.');
      return;
    }

    if (!user) {
      if (width > 860) {
        setPopoverOpen(true);
      } else {
        setMobileMenuOpen(true);
      }
    } else {
      try {
        if (!productVariantData?.productVariant?.id) {
          console.error('Product variant ID is missing.');
          return;
        }
        setDraftOrderLoading(true);
        const result = await createDraftOrder({
          variables: {
            input: {
              user: user.id,
              lines: [
                {
                  quantity: 1,
                  variantId: productVariantData?.productVariant?.id,
                },
              ],
              channelId: channelData?.channel?.id,
              isFastBuy: true,
              ...(collectionPointId && { collectionPoint: collectionPointId }),
            },
          },
        });

        if (result.data?.draftOrderCreate?.order) {
          router.push(`/order`);
        } else {
          console.error(
            'Ошибка при создании заказа:',
            result.data?.draftOrderCreate?.errors
          );
        }
      } catch (e) {
        console.error('Произошла ошибка:', e);
      } finally {
        setDraftOrderLoading(false);
      }
    }
  };

  const handleBasketClick = async () => {
    // MUST CHECK IT: THROWING ERROR
    if (productVariantData?.productVariant?.availability === 'OUT_OF_STOCK') {
      // toaster.push(message("Товара нет в наличии.", "error"), {
      //   placement: "topEnd",
      //   duration: 3000,
      // });
      toast.error(`Товара нет в наличии.`);

      return;
    }
    if (productVariantData?.productVariant?.availability === 'COMING_SOON') {
      // toaster.push(message("Товара скоро появится.", "info"), {
      //   placement: "topEnd",
      //   duration: 3000,
      // });
      toast.info(`Товар скоро появится.`);

      return;
    }

    // if (!user) {
    // if (width > 860) {
    //   setPopoverOpen(true);
    // } else {
    //   setMobileMenuOpen(true);
    // }
    // } else {

    if (isInCart) {
      router.push('/cart');
      return;
    }

    try {
      if (!productVariantData?.productVariant?.id) {
        console.error('Product variant ID is missing.');
        return;
      }

      setDraftOrderLineLoading(true);

      if (isLoggedIn) {
        if (existingDraftOrder) {
          try {
            const result = await createDraftOrderLine({
              variables: {
                id: existingDraftOrder.id,
                input: [
                  {
                    quantity: 1,
                    variantId: productVariantData?.productVariant?.id,
                  },
                ],
              },
            });

            if (result.data?.orderLinesCreate?.order) {
              setIsInCart(true);
            } else {
              console.error(
                'Ошибка при добавлении товара в существующий черновик заказа:',
                result.data?.orderLinesCreate?.errors
              );
            }
          } catch (error) {
            console.error('Ошибка при выполнении createDraftOrderLine:', error);
          } finally {
            setDraftOrderLineLoading(false);
          }
        } else {
          setDraftOrderLineLoading(true);
          try {
            const result = await createDraftOrder({
              variables: {
                input: {
                  user: user?.id,
                  lines: [
                    {
                      quantity: 1,
                      variantId: productVariantData?.productVariant?.id,
                    },
                  ],
                  channelId: channelData?.channel?.id,
                  isFastBuy: false,
                },
              },
            });

            if (result.data?.draftOrderCreate?.order) {
              setIsInCart(true);
            } else {
              console.error(
                'Ошибка при создании заказа:',
                result.data?.draftOrderCreate?.errors
              );
            }
          } catch (error) {
            console.error('Ошибка при выполнении createDraftOrder:', error);
          } finally {
            setDraftOrderLineLoading(false);
          }
        }
      } else {
        if (!orderId) {
          try {
            const orderGuestData = await createDraftOrder({
              variables: {
                input: {
                  lines: [
                    {
                      variantId: productVariantData?.productVariant?.id,
                      quantity: 1,
                    },
                  ],
                  isFastBuy: false,
                  channelId: channelData?.channel?.id,
                },
              },
            });
            if (orderGuestData.data?.draftOrderCreate?.order) {
              setIsInCart(true);
            } else {
              console.error(
                'Ошибка при создании заказа:',
                orderGuestData.data?.draftOrderCreate?.errors
              );
            }
            const guestOrderId =
              orderGuestData?.data?.draftOrderCreate?.order?.id;
            typeof guestOrderId === 'string' &&
              useGuestStore.getState().setGuestOrderId(guestOrderId);
          } catch (error) {
            console.error(
              'Ошибка при выполнении createGuestDraftOrder:',
              error
            );
          } finally {
            setDraftOrderLineLoading(false);
          }
        } else {
          try {
            const result = await createDraftOrderLine({
              variables: {
                id: orderId,
                input: [
                  {
                    variantId: productVariantData?.productVariant?.id,
                    quantity: 1,
                  },
                ],
              },
            });
            if (result.data?.orderLinesCreate?.order) {
              setIsInCart(true);
            } else {
              console.error(
                'Ошибка при добавлении товара в существующий черновик заказа:',
                result.data?.orderLinesCreate?.errors
              );
            }
          } catch (error) {
            console.error(
              'Ошибка при выполнении createGuestDraftOrderLine:',
              error
            );
          } finally {
            setDraftOrderLineLoading(false);
          }
        }
      }
    } catch (e) {
      console.error('Произошла ошибка:', e);
    }
    // }
  };

  const copyPath = (path: any) => {
    navigator.clipboard
      .writeText(path)
      .then(() => {
        toast.success('Код скопирован.');
      })
      .catch(err => {
        toast.error('An error occurred while copying.');
      });
  };

  const handleCopyCurrentURL = async () => {
    if (!currentURL) {
      toast.error('An error occurred while copying.');
      return;
    }

    try {
      hasMounted && (await navigator.clipboard.writeText(currentURL));
      toast.success('Ссылка на страницу скопирована.');
    } catch (error) {
      console.error('Failed to copy: ', error);
      toast.error('An error occurred while copying.');
    }
  };

  useEffect(() => {
    if (existingDraftOrder?.lines && productVariantData?.productVariant?.id) {
      const productInCart = existingDraftOrder.lines.some(
        line => line.productVariantId === productVariantData?.productVariant?.id
      );
      setIsInCart(productInCart);
    } else {
      setIsInCart(false);
    }
  }, [existingDraftOrder, productVariantData?.productVariant?.id]);

  useEffect(() => {
    const newBreadData: BreadcrumbItem[] = [
      {
        title: tBreadcrumb('main'),
        link: '/',
      },
    ];

    const ancestors =
      productVariantData?.productVariant?.product?.category?.ancestors?.edges
        ?.map(edge => edge?.node)
        ?.sort((a, b) => a.level - b.level);

    ancestors?.forEach(node => {
      const id = decodeBase64Id(node.id);
      let link = '';

      if (node.level === 0) {
        link = `/catalog/${id}-${node.slug}`;
      } else if (node.level === 1) {
        link = `/category/${id}-${node.slug}`;
      }

      newBreadData.push({
        title:
          locale === 'ru' ? (node?.name ?? 'Unknown') : node?.translation?.name,
        link,
      });
    });

    newBreadData.push({
      title:
        locale === 'ru'
          ? productVariantData?.productVariant?.name
          : productVariantData?.productVariant?.translation?.name,
      link: pathname,
    });

    setBreadData(newBreadData);
  }, [productVariantData, pathname]);

  const handleScrollToSecondaryInfo = () => {
    const element = document.getElementById('secondaryInfoSection');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const [selectedColor, setSelectedColor] = useState<string | null | undefined>(
    null
  );
  const [selectedStorage, setSelectedStorage] = useState<
    string | null | undefined
  >(null);

  const variants = productData?.product?.variants;

  const hasColorAttribute = useMemo(() => {
    if (!variants) return '';

    for (const variant of variants) {
      if (variant.attributes) {
        for (const attr of variant.attributes) {
          if (attr.attribute.inputType === 'SWATCH') {
            return locale === 'ru'
              ? attr.attribute.name || ''
              : attr?.attribute?.translation?.name || '';
          }
        }
      }
    }

    return '';
  }, [variants]);

  const hasStorageAttribute = useMemo(() => {
    if (!variants) return '';

    for (const variant of variants) {
      if (variant.attributes) {
        for (const attr of variant.attributes) {
          if (attr.attribute.inputType === 'DROPDOWN') {
            return locale === 'ru'
              ? attr.attribute.name || ''
              : attr?.attribute?.translation?.name || '';
          }
        }
      }
    }

    return '';
  }, [variants]);

  const getAttributeValue = (
    variant: ProductVariant,
    attributeName: string
  ) => {
    const attribute = variant?.attributes?.find(attr => {
      const attrName =
        locale === 'ru'
          ? attr?.attribute?.name
          : attr?.attribute?.translation?.name;
      return attrName === attributeName;
    });

    if (!attribute) {
      return null;
    }

    if (attribute?.attribute?.inputType === 'SWATCH') {
      return attribute?.values?.[0]?.value;
    } else if (attribute?.attribute?.inputType === 'DROPDOWN') {
      return attribute?.values?.[0]?.name;
    }

    return attribute?.values?.[0];
  };

  const colors = useMemo(() => {
    return variants?.reduce((acc: Color[], variant: unknown) => {
      if (typeof variant === 'object' && variant !== null) {
        const typedVariant = variant as ProductVariant;
        const colorValue = getAttributeValue(typedVariant, hasColorAttribute);

        const colorName =
          locale === 'ru'
            ? typedVariant?.attributes?.find(attr => {
                const attrName =
                  locale === 'ru'
                    ? attr?.attribute?.name
                    : attr?.attribute?.translation?.name;
                return attrName === hasColorAttribute;
              })?.values?.[0]?.name
            : typedVariant?.attributes?.find(attr => {
                const attrName =
                  locale === 'ru'
                    ? attr?.attribute?.name
                    : attr?.attribute?.translation?.name;
                return attrName === hasColorAttribute;
              })?.values?.[0]?.translation?.name;

        const color: Color = { value: colorValue, name: colorName };
        return colorValue && !acc.find(c => c.value === colorValue)
          ? [...acc, color]
          : acc;
      }
      return acc;
    }, [] as Color[]);
  }, [variants]);

  const storages = useMemo(() => {
    return variants?.reduce((acc: string[], variant: unknown) => {
      if (typeof variant === 'object' && variant !== null) {
        const typedVariant = variant as ProductVariant;
        const storage = getAttributeValue(
          typedVariant,
          hasStorageAttribute
        ) as string;
        return storage && !acc.includes(storage) ? [...acc, storage] : acc;
      }
      return acc;
    }, [] as string[]);
  }, [variants]);

  const [availableStoragesForColor, setAvailableStoragesForColor] = useState<{
    [key: string]: (Maybe<string> | AttributeValue | undefined)[];
  }>({});
  const [availableColorsForStorage, setAvailableColorsForStorage] = useState<{
    [key: string]: (Maybe<string> | AttributeValue | undefined)[];
  }>({});

  useEffect(() => {
    if (productVariantData?.productVariant) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        ecommerce: {
          currencyCode: 'UZS',
          detail: {
            products: [
              {
                id: productVariantData.productVariant.id,
              },
            ],
          },
        },
      });
    }
  }, [productVariantData]);

  useEffect(() => {
    if (variants && productId) {
      const defaultVariant = variants.find(
        variant => variant.id === productId
      ) as unknown as ProductVariant;
      const colorName = locale === 'uz';
      if (defaultVariant) {
        const defaultColorValue = getAttributeValue(
          defaultVariant,
          hasColorAttribute
        );
        const defaultStorage = getAttributeValue(
          defaultVariant,
          hasStorageAttribute
        );

        const defaultColorName = colors?.find(
          c => c.value === defaultColorValue
        )?.name;
        setSelectedColor(defaultColorName);
        setSelectedStorage(
          typeof defaultStorage === 'string' ? defaultStorage : null
        );
      }
    }
  }, [variants, productId, colors]);

  useEffect(() => {
    if (variants && colors) {
      const availableStorages: {
        [key: string]: (Maybe<string> | AttributeValue | undefined)[];
      } = {};
      colors.forEach(color => {
        if (color.name) {
          availableStorages[color.name] = variants
            .filter(variant => {
              if (typeof variant === 'object' && variant !== null) {
                const typedVariant = variant as unknown as ProductVariant;
                return (
                  getAttributeValue(typedVariant, hasColorAttribute) ===
                  color.value
                );
              }
              return false;
            })
            .map(variant => {
              if (typeof variant === 'object' && variant !== null) {
                const typedVariant = variant as unknown as ProductVariant;
                return getAttributeValue(typedVariant, hasStorageAttribute);
              }
              return undefined;
            });
        }
      });
      setAvailableStoragesForColor(availableStorages);
    }
  }, [variants, colors]);

  useEffect(() => {
    if (variants && colors) {
      const availableColors: {
        [key: string]: (Maybe<string> | AttributeValue | undefined)[];
      } = {};
      storages?.forEach(storage => {
        availableColors[storage] = variants
          .filter(variant => {
            if (typeof variant === 'object' && variant !== null) {
              const typedVariant = variant as unknown as ProductVariant;
              return (
                getAttributeValue(typedVariant, hasStorageAttribute) === storage
              );
            }
            return false;
          })
          .map(variant => {
            if (typeof variant === 'object' && variant !== null) {
              const typedVariant = variant as unknown as ProductVariant;
              const colorValue = getAttributeValue(
                typedVariant,
                hasColorAttribute
              );
              return colors.find(c => c.value === colorValue)?.value;
            }
            return undefined;
          });
      });
      setAvailableColorsForStorage(availableColors);
    }
  }, [variants, colors, storages]);

  const getColorValueByName = (colorValue: string | null | undefined) => {
    const colorObj = colors?.find(c => c.value === colorValue);
    const colorName = colorObj ? colorObj.name : null;
    return colorName;
  };

  const handleColorChange = (colorValue: string | null | undefined) => {
    const colorName = getColorValueByName(colorValue);
    setSelectedColor(current => (current === colorName ? null : colorName));
  };

  const handleStorageChange = (storage: string | null | undefined) => {
    setSelectedStorage(current => (current === storage ? null : storage));
  };

  const findVariantByColorAndStorage = (
    variants: ProductVariant[] | undefined,
    selectedColor: string | null | undefined,
    selectedStorage: string | null | undefined
  ): ProductVariant | undefined => {
    if (!variants || !selectedColor || !selectedStorage) {
      return undefined;
    }

    return variants.find(variant => {
      const colorValue = getAttributeValue(variant, hasColorAttribute);
      const colorName = colors?.find(c => c.value === colorValue)?.name;
      const storage = getAttributeValue(variant, hasStorageAttribute);
      return colorName === selectedColor && storage === selectedStorage;
    }) as ProductVariant | undefined;
  };

  useEffect(() => {
    if (!variants) return;

    const typedVariants = variants as unknown as ProductVariant[];

    const hasBothAttributes = hasColorAttribute && hasStorageAttribute;

    let foundVariant: ProductVariant | undefined;

    if (hasBothAttributes) {
      foundVariant = findVariantByColorAndStorage(
        typedVariants,
        selectedColor,
        selectedStorage
      );
    } else if (hasColorAttribute) {
      foundVariant = typedVariants.find(variant => {
        const colorValue = getAttributeValue(variant, hasColorAttribute);
        const colorName = colors?.find(c => c.value === colorValue)?.name;
        return colorName === selectedColor;
      }) as ProductVariant | undefined;
    } else if (hasStorageAttribute) {
      foundVariant = typedVariants.find(variant => {
        const storage = getAttributeValue(variant, hasStorageAttribute);
        return storage === selectedStorage;
      }) as ProductVariant | undefined;
    }

    if (foundVariant) {
      if (foundVariant.id !== currentProductId) {
        setCurrentProductId(foundVariant.id);
        const newUrl = `/product/${decodeBase64Id(foundVariant.id)}-${productData?.product?.slug}`;

        router.push(newUrl);
        refetch({ id: foundVariant.id, channel: 'default-channel' })
          .then(() =>
            console.log('Refetch complete with ID:', foundVariant?.id)
          )
          .catch(error => console.error('Refetch error:', error));
      }
    } else {
      console.log('No variant found for selected attributes.');
    }
  }, [
    variants,
    selectedColor,
    selectedStorage,
    refetch,
    currentProductId,
    hasColorAttribute,
    hasStorageAttribute,
  ]);

  const [activeTabFromButton, setActiveTabFromButton] =
    useState<ProductDefaultTabEnum>(ProductDefaultTabEnum.SPECIFICATIONS);

  const handleButtonClick = useCallback(
    (tabIndex: ProductDefaultTabEnum) => {
      setActiveTabFromButton(tabIndex);
      handleScrollToSecondaryInfo();
    },
    [setActiveTabFromButton]
  );

  useEffect(() => {
    if (user && productVariantData?.productVariant?.id) {
      addViewedVariant({
        variables: {
          variant: productVariantData?.productVariant?.id,
        },
        context: {
          requiresAuth: true,
        },
      });
    }
  }, [user, productVariantData, addViewedVariant]);

  useEffect(() => {
    if (productData?.product?.defaultTab) {
      setActiveTabFromButton(productData.product.defaultTab);
    }
  }, [productData]);

  const handleScrollToInStoreAvailability = () => {
    if (inStoreAvailabilityRef.current) {
      inStoreAvailabilityRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  useEffect(() => {
    const isMobile = width <= 480;
    const isDesktop = width <= 1040;
    const targetEl = isMobile ? mobileRef.current : desktopRef.current;
    const threshold = isMobile ? 0.1 : isDesktop ? 0 : 0.3;

    if (!targetEl) return;

    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyHeader(entry.isIntersecting),
      {
        root: null,
        threshold,
      }
    );

    if (targetEl) {
      observer.observe(targetEl);
    }

    return () => {
      if (targetEl) {
        observer.unobserve(targetEl);
      }
    };
  }, [width]);

  const matchedCityTashkent = locale === 'uz' ? 'Tashkent' : 'Ташкент';
  const matchedCityAndijan = locale === 'uz' ? 'Andijan' : 'Андижан';
  const [activeIndex, setActiveIndex] = useState(0);
  const steps = [3, 6, 9, 11, 12, 15, 18, 24];

  return (
    <>
      <StickyHeader
        id={productId}
        isInCart={isInCart}
        loadingBasket={draftOrderLineLoading}
        loadingBuyNow={draftOrderLoading}
        productName={
          locale === 'ru'
            ? productVariantData?.productVariant?.name
            : productVariantData?.productVariant?.translation?.name
        }
        productPrice={
          productVariantData?.productVariant?.pricing?.priceUndiscounted?.net
            .amount
        }
        productImage={
          productVariantData?.productVariant?.media?.[0]?.url
            ? productVariantData?.productVariant?.media?.[0]?.url
            : DefaultProductPlaceholder?.src
        }
        showHeader={showStickyHeader}
        onBasketClick={handleBasketClick}
        onBuyNowClick={() => handleClick()}
      />

      <div className={styles.productWrapper}>
        {loading ? (
          <div className={styles.skeleton}>
            <Skeleton width={'100%'} />
          </div>
        ) : (
          <Breadcrumbs data={breadData} />
        )}
        <div className={styles.productHeader}>
          <div className={styles.header}>
            {loading ? (
              <h2 className={`${styles.title} ${styles.skeleton}`}>
                <Skeleton width={'100%'} />
              </h2>
            ) : (
              <h2 className={styles.title}>
                {locale === 'uz'
                  ? productVariantData?.productVariant?.translation?.name
                  : productVariantData?.productVariant?.name}
              </h2>
            )}
            <div className={styles.codeBrand}>
              {loading ? (
                <Skeleton
                  className={` ${styles.skeletonForLabels}`}
                  width={'100px'}
                />
              ) : (
                <>
                  {t('code')}: {productVariantData?.productVariant?.sku}&nbsp;
                  <Button
                    type="button"
                    onClick={() =>
                      copyPath(productVariantData?.productVariant?.sku)
                    }
                    className={styles.iconCopy}
                  >
                    <IconCopy />
                  </Button>
                </>
              )}
            </div>
            <div className={styles.ratingsAndLinks}>
              <div className={styles.rating}>
                <Rate
                  value={productData?.product?.rating ?? 0}
                  character={<IconStar />}
                  size="xs"
                  readOnly
                  color="yellow"
                />
                <Link href={'#'} className={styles.comment}>
                  {productData?.product?.activeReviewsCount != null ? (
                    <ReviewsText
                      locale={locale}
                      count={productData.product.activeReviewsCount}
                    />
                  ) : (
                    t('no-reviews')
                  )}
                </Link>
              </div>
              <div className={styles.links}>
                <Favorite id={currentProductId} />
                <Button
                  type="button"
                  className={styles.btn}
                  onClick={handleCopyCurrentURL}
                >
                  <IconExport />
                </Button>
              </div>
            </div>
          </div>
          <div className={styles.brandName}>
            {loading ? (
              <Skeleton
                className={`${styles.name} ${styles.skeletonForBrand}`}
              />
            ) : (
              <div className={styles.name}>
                <Image
                  src={`${config.publicApiUrl}${productVariantData?.productVariant?.product?.brand?.image}`}
                  alt="brand"
                  width={76}
                  height={22}
                  style={{ objectFit: 'contain' }}
                />
              </div>
            )}
            <div className={styles.codeBrand}>
              {loading ? (
                <Skeleton
                  className={` ${styles.skeletonForLabels}`}
                  width={'100px'}
                />
              ) : (
                <>
                  {t('code')}: {productVariantData?.productVariant?.sku}&nbsp;
                  <Button
                    type="button"
                    onClick={() =>
                      copyPath(productVariantData?.productVariant?.sku)
                    }
                    className={styles.iconCopy}
                  >
                    <IconCopy />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
        <div className={styles.productInfo}>
          <div className={styles.leftSide}>
            <div className={styles.tags}>
              {/* <div className={styles.tagPromotion}>Акция</div> */}
              {bonus !== undefined && bonus !== null && bonus !== 0 && (
                <div className={styles.bonus}>{t('bonus')}</div>
              )}
              {productVariantData?.productVariant?.pricing?.discount && (
                <div className={styles.tagDiscount}>{t('discount')}</div>
              )}
              {/* <div className={styles.tagGift}>Redmi Buds 4 в подарок</div> */}

              {loading ? (
                <>
                  <Skeleton
                    className={`${styles.labels} ${styles.skeletonForLabels}`}
                    width={'100px'}
                  />
                  <Skeleton
                    className={`${styles.labels} ${styles.skeletonForLabels}`}
                    width={'80px'}
                  />
                  <Skeleton
                    className={`${styles.labels} ${styles.skeletonForLabels}`}
                    width={'60px'}
                  />
                </>
              ) : (
                productVariantData?.productVariant?.labels?.edges?.map(
                  label => {
                    return (
                      <div
                        className={styles.labels}
                        style={{
                          backgroundColor: `${label?.node?.backgroundColor} `,
                          color: `${label?.node?.color}`,
                        }}
                        key={label?.node?.id}
                      >
                        {label?.node?.content}
                      </div>
                    );
                  }
                )
              )}
            </div>
            <div className={styles.carouselWrapper}>
              <div className={styles.carousel}>
                <ProductCarousel
                  media={
                    productVariantData?.productVariant?.media
                      ? productVariantData?.productVariant?.media
                      : placeholderImageForCarousel
                  }
                />
              </div>
            </div>
          </div>
          <div className={styles.rightSide}>
            <div className={styles.infoRightAll}>
              <div className={styles.contentWrapper}>
                <div className={styles.header}>
                  {loading ? (
                    <h2 className={`${styles.title} ${styles.skeleton}`}>
                      <Skeleton width={'100%'} />
                    </h2>
                  ) : (
                    <h2 className={styles.title}>
                      {locale === 'ru'
                        ? productVariantData?.productVariant?.name
                        : productVariantData?.productVariant?.translation?.name}
                    </h2>
                  )}
                  <div className={styles.ratingsAndLinks}>
                    <div className={styles.rating}>
                      <Rate
                        value={productData?.product?.rating ?? 0}
                        character={<IconStar />}
                        readOnly
                        size="xs"
                        color="yellow"
                      />
                      <Link href={'#'} className={styles.comment}>
                        {productData?.product?.activeReviewsCount != null ? (
                          <ReviewsText
                            locale={locale}
                            count={productData?.product?.activeReviewsCount}
                          />
                        ) : (
                          t('no-reviews')
                        )}
                      </Link>
                    </div>
                    <div className={styles.links}>
                      <Favorite id={currentProductId} />
                      <Button
                        type="button"
                        className={styles.btn}
                        onClick={handleCopyCurrentURL}
                      >
                        <IconExport />
                      </Button>
                    </div>
                  </div>
                </div>
                {loading ? (
                  <div className={styles.memory}>
                    <h4 className={styles.title}>{t('embedded-memory')}</h4>
                    <div className={styles.memories}>
                      {[...Array(3)].map((_, index) => (
                        <Skeleton
                          key={index}
                          width={101}
                          height={52}
                          className={styles.skeletonForLabels}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  hasStorageAttribute !== '' && (
                    <div className={styles.memory}>
                      <h4 className={styles.title}>{hasStorageAttribute}</h4>
                      <div className={styles.memories}>
                        {storages?.map(storage => (
                          <Button
                            key={storage}
                            type="button"
                            className={`${styles.btnMemory} ${
                              selectedStorage !== storage
                                ? styles.btnActive
                                : ''
                            } ${
                              selectedColor &&
                              !(
                                availableStoragesForColor[selectedColor] || []
                              ).includes(storage)
                                ? styles.unavailable
                                : ''
                            }`}
                            onClick={() => handleStorageChange(storage)}
                            disabled={
                              selectedColor
                                ? !(
                                    availableStoragesForColor[selectedColor] ||
                                    []
                                  ).includes(storage)
                                : undefined
                            }
                          >
                            {storage}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )
                )}
                {loading ? (
                  <div className={styles.colors}>
                    <h3>
                      {t('color')}:{' '}
                      <Skeleton
                        width={125}
                        height={17}
                        className={styles.skeletonForLabels}
                      />
                    </h3>
                    <div className={styles.checkboxes}>
                      {[...Array(3)].map((_, index) => (
                        <Skeleton
                          key={index}
                          width={25}
                          height={25}
                          className={styles.skeletonForLabels}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  hasColorAttribute !== '' && (
                    <div className={styles.colors}>
                      <h3>
                        {hasColorAttribute}:{' '}
                        <span>{selectedColor || t('non-selected')}</span>
                      </h3>
                      <div className={styles.checkboxes}>
                        {colors?.map(color => (
                          <div key={color?.value as string}>
                            <input
                              type="radio"
                              name="color"
                              id={color?.value as string}
                              value={color?.value as string}
                              onChange={() => {}}
                              checked={selectedColor === color?.name}
                              onClick={() =>
                                handleColorChange(
                                  typeof color?.value === 'string'
                                    ? color?.value
                                    : undefined
                                )
                              }
                              disabled={
                                selectedStorage
                                  ? !(
                                      availableColorsForStorage[
                                        selectedStorage
                                      ] || []
                                    ).includes(color?.value)
                                  : undefined
                              }
                            />
                            <label htmlFor={color?.value as string}>
                              <span
                                style={{
                                  backgroundColor: `${color?.value}`,
                                }}
                                className={`${
                                  selectedStorage &&
                                  !(
                                    availableColorsForStorage[
                                      selectedStorage
                                    ] || []
                                  ).includes(color?.value)
                                    ? styles.unavailable
                                    : ''
                                }`}
                              ></span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                )}
                {productVariantData?.productVariant?.gifts &&
                  Array.isArray(productVariantData?.productVariant?.gifts) &&
                  productVariantData?.productVariant?.gifts?.length > 0 && (
                    <div className={styles.gifts}>
                      <div className={styles.title}>
                        <IconGift />
                        <h4>{t('gift-for-buying')}</h4>
                        <IconInfo />
                      </div>
                      {productVariantData?.productVariant?.gifts?.map(gift => {
                        return (
                          <Link
                            href={`/product/${gift?.product.slug}/${decodeBase64Id(
                              gift?.product?.id ?? '#'
                            )}/${decodeBase64Id(gift?.id ?? '#')}`}
                            key={gift?.id}
                          >
                            <div className={styles.gift}>
                              {gift?.media && (
                                <Image
                                  width={72}
                                  height={62}
                                  src={
                                    gift?.media[0]?.url
                                      ? gift?.media[0]?.url
                                      : DefaultProductPlaceholder
                                  }
                                  alt="gift-image"
                                  style={{ objectFit: 'contain' }}
                                />
                              )}
                              <p>
                                {locale === 'ru'
                                  ? gift?.name
                                  : gift?.translation?.name}
                              </p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                {/* <div className={styles.gifts}>
								<div className={styles.title}>
									<IconGift />
									<h4>Подарок за покупку</h4>
									<IconInfo />
								</div>
								<div className={styles.gift}>
									<Image src={ImageIrpods} alt="gift-image" />
									<p>Беспроводные наушники Redmi Buds 4 Active (черный)</p>
								</div>
							</div> */}
                {hasMounted && width <= 840 && (
                  <div className={styles.personalPrice}>
                    <div className={styles.category}>
                      {loading ? (
                        <Skeleton
                          className={` ${styles.skeletonForLabels}`}
                          width={'250px'}
                        />
                      ) : (
                        <div className={styles.category}>
                          {productVariantData?.productVariant?.availability && (
                            <AvailabilityIndicator
                              availability={
                                productVariantData?.productVariant?.availability
                              }
                            />
                          )}
                        </div>
                      )}
                    </div>

                    <div className={styles.top}>
                      <div className={styles.priceAndOldPrice}>
                        {loading ? (
                          <b>
                            <Skeleton
                              className={styles.skeletonForLabels}
                              width={280}
                              height={32}
                            />
                          </b>
                        ) : (
                          <>
                            {priceUndiscounted !== price && price > 0 && (
                              <b
                                className={
                                  discount ? styles.discount : styles.price
                                }
                              >
                                {convertToReadable(price)} {t('price')}
                              </b>
                            )}
                            {discount > 0 && price > 0 ? (
                              <p className={styles.price}>
                                {convertToReadable(priceUndiscounted)}{' '}
                                {t('price')}
                              </p>
                            ) : null}
                            {priceUndiscounted === discount && price === 0 && (
                              <b className={styles.price}>
                                {convertToReadable(priceUndiscounted)}{' '}
                                {t('price')}
                              </b>
                            )}
                            {priceUndiscounted === price && discount === 0 && (
                              <b>
                                {convertToReadable(priceUndiscounted)}{' '}
                                {t('price')}
                              </b>
                            )}
                          </>
                        )}
                      </div>
                      {productVariantData?.productVariant?.pricing
                        ?.bonusesAmount !== undefined &&
                        productVariantData?.productVariant?.pricing
                          ?.bonusesAmount !== null &&
                        productVariantData?.productVariant?.pricing
                          ?.bonusesAmount !== 0 && (
                          <div className={styles.cashback}>
                            <div className={styles.tag}>
                              +
                              {convertToReadable(
                                productVariantData?.productVariant?.pricing
                                  ?.bonusesAmount
                              )}{' '}
                              {t('price')}
                            </div>
                            <span>{t('cashback')}</span>
                          </div>
                        )}
                    </div>
                    <div className={styles.bottom}>
                      <Button
                        type="button"
                        className={styles.btnAdd}
                        onClick={handleBasketClick}
                        loading={draftOrderLineLoading}
                      >
                        {isInCart ? t('product-in-cart') : t('add-to-cart')}
                      </Button>
                      <Button
                        type="button"
                        className={styles.btnBuyNow}
                        onClick={() => handleClick()}
                        loading={draftOrderLoading}
                      >
                        {t('buy-now')}
                      </Button>
                    </div>
                  </div>
                )}
                <div className={styles.characteristics}>
                  <h4>{t('features')}</h4>
                  {loading
                    ? [...Array(5)].map((_, index) => (
                        <div className={styles.detail} key={index}>
                          <div className={`${styles.skeletonForAtt}`}>
                            <Skeleton />
                          </div>
                        </div>
                      ))
                    : productVariantData?.productVariant?.product?.attributes?.map(
                        att => {
                          if (att.values.length === 0) return null;
                          if (att.attribute.isMain)
                            return (
                              <div
                                className={styles.detail}
                                key={att?.attribute?.id}
                              >
                                <div className={styles.typeDetail}>
                                  <h5>
                                    {locale === 'ru'
                                      ? att?.attribute?.name
                                      : att?.attribute?.translation?.name}
                                  </h5>{' '}
                                  <span></span>
                                </div>
                                <div className={styles.infoDetail}>
                                  {locale === 'ru'
                                    ? att?.values[0]?.name
                                    : att?.values[0]?.translation?.name}
                                </div>
                              </div>
                            );
                        }
                      )}

                  <div
                    onClick={() =>
                      handleButtonClick(ProductDefaultTabEnum.SPECIFICATIONS)
                    }
                    className={styles.moreDetails}
                  >
                    {t('all-features')}
                  </div>
                </div>
              </div>
              <div className={styles.infoRight}>
                <div className={styles.brandName}>
                  {loading ? (
                    <Skeleton
                      className={`${styles.name} ${styles.skeletonForBrand}`}
                    />
                  ) : (
                    // <Link href={`/brand/${productData?.product?.brand?.id}`}>
                    <div className={styles.name}>
                      <Image
                        src={`${config.publicApiUrl}${productVariantData?.productVariant?.product?.brand?.image}`}
                        alt="brand"
                        width={76}
                        height={22}
                        style={{ objectFit: 'contain' }}
                      />
                    </div>
                    // </Link>
                  )}
                  <div className={styles.codeBrand}>
                    {loading ? (
                      <Skeleton
                        className={` ${styles.skeletonForLabels}`}
                        width={'100px'}
                      />
                    ) : (
                      <>
                        {t('code')}: {productVariantData?.productVariant?.sku}
                        &nbsp;
                        <Button
                          type="button"
                          onClick={() =>
                            copyPath(productVariantData?.productVariant?.sku)
                          }
                          className={styles.iconCopy}
                        >
                          <IconCopy />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                {hasMounted && width > 840 && (
                  <div className={styles.personalPrice}>
                    <div className={styles.category}>
                      {loading ? (
                        <Skeleton
                          className={` ${styles.skeletonForLabels}`}
                          width={'250px'}
                        />
                      ) : (
                        <div className={styles.category}>
                          {productVariantData?.productVariant?.availability && (
                            <AvailabilityIndicator
                              availability={
                                productVariantData?.productVariant?.availability
                              }
                            />
                          )}
                        </div>
                      )}
                    </div>

                    <div className={styles.top}>
                      <div className={styles.priceAndOldPrice}>
                        {loading ? (
                          <b>
                            <Skeleton
                              className={styles.skeletonForLabels}
                              width={280}
                              height={32}
                            />
                          </b>
                        ) : (
                          <>
                            {priceUndiscounted !== price && price > 0 && (
                              <b
                                className={
                                  discount ? styles.discount : styles.price
                                }
                              >
                                {convertToReadable(price)} {t('price')}
                              </b>
                            )}
                            {discount > 0 && price > 0 ? (
                              <p className={styles.price}>
                                {convertToReadable(priceUndiscounted)}{' '}
                                {t('price')}
                              </p>
                            ) : null}
                            {priceUndiscounted === discount && price === 0 && (
                              <b className={styles.price}>
                                {convertToReadable(priceUndiscounted)}{' '}
                                {t('price')}
                              </b>
                            )}
                            {priceUndiscounted === price && discount === 0 && (
                              <b>
                                {convertToReadable(priceUndiscounted)}{' '}
                                {t('price')}
                              </b>
                            )}
                          </>
                        )}
                      </div>
                      {productVariantData?.productVariant?.pricing
                        ?.bonusesAmount !== undefined &&
                        productVariantData?.productVariant?.pricing
                          ?.bonusesAmount !== null &&
                        productVariantData?.productVariant?.pricing
                          ?.bonusesAmount !== 0 && (
                          <div className={styles.cashback}>
                            <div className={styles.tag}>
                              +
                              {convertToReadable(
                                productVariantData?.productVariant?.pricing
                                  ?.bonusesAmount
                              )}{' '}
                              {t('price')}
                            </div>
                            <span>{t('cashback')}</span>
                          </div>
                        )}
                    </div>
                    <div className={styles.bottom}>
                      <Button
                        type="button"
                        className={styles.btnAdd}
                        onClick={handleBasketClick}
                        loading={draftOrderLineLoading}
                      >
                        {isInCart ? t('product-in-cart') : t('add-to-cart')}
                      </Button>
                      <Button
                        type="button"
                        className={styles.btnBuyNow}
                        onClick={() => handleClick()}
                        loading={draftOrderLoading}
                      >
                        {t('buy-now')}
                      </Button>
                    </div>
                  </div>
                )}
                {!user && (
                  <div className={styles.registerAndSignIn}>
                    <p>{t('auth-helper-text')}</p>
                    <div className={styles.links} onClick={() => handleClick()}>
                      <span className={styles.link}>{t('login')}</span>
                      <span className={styles.separator}>/</span>
                      <span className={styles.link}>{t('register')}</span>
                    </div>
                  </div>
                )}
                {/* <div className={styles.installmentWrapper}>
                  <h4>
                    Рассрочка на {steps[activeIndex]} месяцев
                  </h4>

                  <div className={styles.stepSelector}>
                    {steps.map((step, index) => {
                      const isButtonActive = index <= activeIndex;
                      const isLineActive = index < activeIndex;
                      const isLast = index === steps.length - 1;

                      return (
                        <div key={step} className={styles.stepWrapper}>
                          <span className={styles.stepLabel}>{step}</span>
                          <div className={styles.buttonWrapper}>
                            <button
                              className={`${styles.stepButton} ${
                                isButtonActive ? styles.active : styles.inactive
                              }`}
                              onClick={() => setActiveIndex(index)}
                            />
                            {!isLast && (
                              <div
                                className={`${styles.stepLine} ${
                                  isLineActive ? styles.active : styles.inactive
                                }`}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div> */}
                {productVariantData?.productVariant?.pricing?.installmentPrice4
                  ?.amount !== undefined &&
                  productVariantData?.productVariant?.pricing?.installmentPrice4
                    ?.amount !== null &&
                  productVariantData?.productVariant?.pricing?.installmentPrice4
                    ?.amount !== 0 && (
                    <div className={styles.splitPayment}>
                      <div className={styles.top}>
                        <div className={styles.parent}>
                          <b className={styles.bold}>
                            {t('pay-in-installments')}
                          </b>
                          <p className={styles.perMonth}>
                            {t('price-per-month', {
                              price: convertToReadable(
                                Math.round(
                                  productVariantData?.productVariant?.pricing
                                    ?.installmentPrice4?.amount / 4
                                )
                              ),
                            })}
                          </p>
                        </div>
                        <Button
                          type="button"
                          className={styles.button}
                          onClick={() =>
                            handleButtonClick(
                              ProductDefaultTabEnum.INSTALLMENTS_AND_CREDIT
                            )
                          }
                        >
                          {t('formalize')}
                        </Button>
                      </div>
                      <div className={styles.middle}>
                        {' '}
                        <div className={styles.priceNow}>{`${convertToReadable(
                          Math.round(
                            productVariantData?.productVariant?.pricing
                              ?.installmentPrice4?.amount / 4
                          )
                        )} ${t('price-today')} `}</div>
                        <div className={styles.priceAll}>
                          {t('and')}{' '}
                          {convertToReadable(
                            productVariantData?.productVariant?.pricing
                              ?.installmentPrice4?.amount -
                              Math.round(
                                productVariantData?.productVariant?.pricing
                                  ?.installmentPrice4?.amount / 4
                              )
                          )}{' '}
                          {t('later')}
                        </div>
                      </div>
                      <div className={styles.bottom}>
                        <div className={styles.months}>
                          <div className={styles.indicator}></div>
                          <div className={styles.dateAndAmountOfPayment}>
                            <div className={styles.div}>{t('today')}</div>
                            <div className={styles.div1}>
                              {convertToReadable(
                                Math.round(
                                  productVariantData?.productVariant?.pricing
                                    ?.installmentPrice4?.amount / 4
                                )
                              )}
                            </div>
                          </div>
                        </div>
                        {Array.from({ length: 3 }).map((_, index) => {
                          const futureDate = new Date();
                          futureDate.setMonth(
                            futureDate.getMonth() + index + 1
                          );
                          const day = futureDate.getDate();
                          const month = futureDate.toLocaleString('ru-RU', {
                            month: 'long',
                          });

                          return (
                            <div className={styles.months} key={index}>
                              <div
                                className={`${styles.indicator} ${styles.indicatorToday}`}
                              />
                              <div className={styles.dateAndAmountOfPayment}>
                                <div className={styles.div}>
                                  {day} {month}
                                </div>
                                <div className={styles.div3}>
                                  {convertToReadable(
                                    Math.round(
                                      (productVariantData?.productVariant
                                        ?.pricing?.installmentPrice4?.amount ??
                                        0) / 4
                                    )
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                <div className={styles.deliveryInfo}>
                  <div className={styles.top}>
                    <div className={styles.div}>
                      <span>{t('get-in')}</span>
                      <CustomCitiesSelect
                        isProductPageIcon={true}
                        textColor="#2563eb"
                        getSelectedValue={(value: string) => setCity(value)}
                      />
                    </div>
                  </div>
                  <div className={styles.list}>
                    {cityShippingMethod?.isYandex && (
                      <div className={styles.item}>
                        <div className={styles.left}>
                          <div className={styles.icon}>
                            <IconYandex />
                          </div>
                          <div className={styles.title}>
                            <div className={styles.div1}>
                              {t('shipping-methods.yandex.title')}
                            </div>
                            <div className={styles.div2}>
                              {t('shipping-methods.yandex.duration')}
                            </div>
                          </div>
                        </div>
                        <div className={styles.div3}>
                          {t('price-from', {
                            price:
                              cityShippingMethod?.yandexDeliveryPrice?.toLocaleString(
                                'ru-RU'
                              ),
                          })}
                        </div>
                      </div>
                    )}
                    {city === matchedCityTashkent ||
                    city === matchedCityAndijan ? (
                      <>
                        <div className={styles.item}>
                          <div className={styles.left}>
                            <div className={styles.icon}>
                              <IconKuryer />
                            </div>
                            <div className={styles.title}>
                              <div className={styles.div1}>
                                {' '}
                                {t('shipping-methods.courier.title')}
                              </div>
                              <div className={styles.div2}>
                                {t('shipping-methods.courier.duration')}{' '}
                                {cityShippingMethod?.timeRanges?.slice(-1)[0]
                                  .endTime || '17:00'}
                              </div>
                            </div>
                          </div>
                          <div className={styles.div3}>
                            {t('shipping-methods.courier.cost', {
                              price:
                                cityShippingMethod?.price.toLocaleString(
                                  'ru-RU'
                                ),
                            })}
                          </div>
                        </div>
                        {productVariantData?.productVariant?.storesAvailable !==
                          0 && (
                          <div className={styles.item2}>
                            <div className={styles.left}>
                              <div className={styles.icon}>
                                <IconShop />
                              </div>
                              <div className={styles.title}>
                                <div className={styles.div1}>
                                  {t('shipping-methods.pickup.title')}
                                </div>
                                {/* <div className={styles.div2}>
                                  <button
                                    onClick={handleScrollToInStoreAvailability}
                                  >
                                    <span>
                                      {t('shipping-methods.pickup.exist')}{' '}
                                    </span>
                                    <span className={styles.span}>
                                      {
                                        productVariantData?.productVariant
                                          ?.storesAvailable
                                      }{' '}
                                      {t('shipping-methods.pickup.in-stores')}
                                    </span>
                                  </button>
                                </div> */}
                              </div>
                            </div>
                            <div
                              className={`${styles.div3} ${styles.div3Store}`}
                            >
                              {t('free')}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className={`${styles.item} ${styles.hideBorder}`}>
                        <div className={styles.left}>
                          <div className={styles.icon}>
                            <IconBts />
                          </div>
                          <div className={styles.title}>
                            <div className={styles.div1}>
                              {t('shipping-methods.by-post.title')}
                            </div>
                            <div className={styles.div2}>
                              {t('shipping-methods.by-post.duration')}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <ReccomendSection
              brand={
                locale === 'ru'
                  ? productData?.product?.brand?.name
                  : (productData?.product?.brand?.translation?.name as string)
              }
              brandCategories={categoriesData}
              similar={similarProductData}
              loading={loading}
              productLoading={similarProductLoading}
              onCategorySelected={handleCategorySelected}
              ref={mobileRef}
            />
          </div>
        </div>
        <div ref={desktopRef}>
          <SecondaryInfo
            data={productData}
            variantData={productVariantData}
            loading={productLoading}
            id="secondaryInfoSection"
            activeTabFromButton={activeTabFromButton}
            onTabChange={setActiveTabFromButton}
          />
          {userData?.me?.viewedVariants?.edges &&
            userData.me.viewedVariants.edges.length > 0 && (
              <div className={styles.productCategory}>
                <div className={styles.headerSection}>
                  <h3>{t('recent-view')}</h3>
                  <Link className={styles.link} href={`/pages/history`}>
                    {width <= 600 ? t('all') : t('show-all-products')}{' '}
                    <IconArrowRightBlue />
                  </Link>
                </div>
                <div className={styles.cardsWrapper}>
                  {userData?.me?.viewedVariants?.edges
                    ?.slice(0, 5)
                    ?.map((card: any) => {
                      const labels =
                        card?.node?.labels?.edges?.map(
                          (edge: any) => edge?.node
                        ) || [];
                      const imageUrl = card?.node?.media?.[0]?.url || '';
                      return (
                        <div key={card?.node?.id} className={styles.card}>
                          {' '}
                          <CategoryCard
                            id={card?.node?.id}
                            categorySlug={card?.node?.product?.category?.slug}
                            slug={card?.node?.product?.slug}
                            productId={card?.node?.product?.id}
                            image={imageUrl}
                            wide={false}
                            productType={card?.node?.product?.category?.name}
                            title={card?.node?.name}
                            labels={labels}
                            availability={card?.node?.availability}
                            pricePerMonth={
                              card?.node?.pricing?.installmentPrice12?.amount
                            }
                            price={card?.node?.pricing?.price?.net?.amount ?? 0}
                            discount={
                              card?.node?.pricing?.discount?.net?.amount ?? 0
                            }
                            priceUndiscounted={
                              card?.node?.pricing?.priceUndiscounted?.net
                                ?.amount ?? 0
                            }
                            bonus={card?.node?.pricing?.bonusesAmount}
                            className={styles.categoryCard}
                          />
                        </div>
                      );
                    })}
                </div>
                <div className={styles.headerSectionMobile}>
                  <Link className={styles.link} href={`#`}>
                    {t('show-all-products')}
                    <IconArrowRightBlue />
                  </Link>
                </div>
              </div>
            )}
          {/* <div ref={inStoreAvailabilityRef}>
            <InStoreAvailability
              id="inStoreAvailability"
              stocks={productVariantData?.productVariant?.stocks || []}
              productTitle={
                locale === 'uz'
                  ? productVariantData?.productVariant?.translation?.name
                  : productVariantData?.productVariant?.name
              }
              handleOrderClick={handleClick}
            />
          </div> */}
        </div>
      </div>
    </>
  );
};

export default ProductDetails;
